/**
 * Mehrwertsteuer: die Pflicht, der Satz und die Nummer – an einer Stelle.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum das hier steht und nicht im Server oder in der Oberfläche
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Nummer wird an zwei Stellen geprüft: im Formular, damit der Benutzer
 * sofort sieht, dass etwas nicht stimmt, und im Server, weil der Server
 * massgebend ist und nichts glauben darf, was aus einem Browser kommt.
 *
 * Zwei Prüfungen, die dasselbe meinen, driften auseinander – die eine wird
 * angepasst, die andere vergessen. Dann nimmt das Formular etwas an, was der
 * Server abweist, und der Benutzer sieht eine Fehlermeldung an einem Feld,
 * das grün war. Deshalb steht die Regel hier, und beide holen sie sich.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was eine Schweizer MWST-Nummer ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Jedes Unternehmen hat eine UID: `CHE-123.456.789`. Wer
 * mehrwertsteuerpflichtig ist, weist dieselbe Nummer mit dem Zusatz aus:
 * `CHE-123.456.789 MWST`. Es sind nicht zwei Nummern, sondern eine Nummer in
 * zwei Schreibweisen – deshalb gibt es auch nur ein Feld dafür.
 *
 * Gespeichert wird die nackte Form ohne Zusatz. Den Zusatz setzt der Beleg,
 * und nur dann, wenn der Betrieb pflichtig ist. Stünde er in der Datenbank,
 * müsste man ihn beim Wechsel auf «nicht pflichtig» wieder herausrechnen –
 * eine Zeichenkette zu pflegen, die eine Rechtslage behauptet, geht schief.
 *
 * Nicht geprüft wird die Prüfziffer. Die UID trägt eine, und sie liesse sich
 * nachrechnen – aber ein falsch umgesetztes Verfahren weist gültige Nummern
 * ab, und eine abgewiesene richtige Nummer ist schlimmer als eine
 * durchgelassene falsche: die eine hält jemanden auf, die andere fällt beim
 * ersten Beleg auf. Wenn das Verfahren belegt ist, kommt es dazu.
 */

/** Der Normalsatz, wie ihn die Auswahl anbietet. */
export const MWST_NORMALSATZ = 8.1

/** So sieht eine Nummer aus, wenn sie gespeichert ist. */
const FORM = /^CHE-\d{3}\.\d{3}\.\d{3}$/

/**
 * Was der Benutzer tippt, in die gespeicherte Form bringen.
 *
 * Grosszügig beim Lesen, streng beim Schreiben: `che123456789`,
 * `CHE-123.456.789 MWST`, mit oder ohne Punkte, mit Leerzeichen – alles
 * ergibt `CHE-123.456.789`. Wer seine Nummer aus einem Brief abschreibt,
 * soll nicht an einem Punkt scheitern.
 *
 * Was nicht zu deuten ist, kommt getrimmt zurück, wie es kam. Die Prüfung
 * darunter sagt dann, was fehlt – hier wird nichts erraten.
 */
export function normalisiereUid(eingabe: string): string {
  const roh = (eingabe ?? '').trim()
  if (roh === '') return ''
  const ziffern = roh.replace(/\s+/g, '').toUpperCase().replace(/MWST$/, '').replace(/[^0-9]/g, '')
  if (ziffern.length !== 9) return roh
  return `CHE-${ziffern.slice(0, 3)}.${ziffern.slice(3, 6)}.${ziffern.slice(6, 9)}`
}

/** Trägt die Nummer die gültige Form? Leer gilt nicht als gültig. */
export function istUidGueltig(eingabe: string): boolean {
  return FORM.test(normalisiereUid(eingabe))
}

/**
 * Was an der Nummer nicht stimmt – als Satz, den man dem Benutzer zeigt.
 *
 * `undefined`, wenn sie stimmt. Der Satz nennt die erwartete Form, statt nur
 * «ungültig» zu sagen: wer «CHE 123456789» getippt hat, sieht daran sofort,
 * was gemeint ist.
 */
export function uidFehler(eingabe: string): string | undefined {
  const roh = (eingabe ?? '').trim()
  if (roh === '') return 'Die MWST-Nummer fehlt. Sie steht in der Form CHE-123.456.789.'
  if (!istUidGueltig(roh))
    return 'Das ist keine gültige MWST-Nummer. Erwartet wird die Form CHE-123.456.789 – neun Ziffern nach CHE.'
  return undefined
}

/**
 * Die Nummer, wie sie auf den Beleg gehört.
 *
 * Mit dem Zusatz, weil sie dort als Mehrwertsteuernummer auftritt und nicht
 * als blosse Unternehmensnummer. Leer, wenn keine Nummer da ist – dann steht
 * auf dem Beleg nichts statt einer halben Zeile.
 */
export function uidFuerBeleg(uid: string): string {
  const n = normalisiereUid(uid)
  return FORM.test(n) ? `${n} MWST` : ''
}
